import { useCallback } from 'react';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { getATLContextUrl } from '@atlassian/atl-context';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller';
import { APP_IDENTIFIER } from '../../common/constants';
import {
	type SupportedThirdParty,
	MONDAY,
	ASANA,
	CLICK_UP,
} from '../../common/constants/import-type';
import { EXPERIENCES } from '../../common/constants/metrics';

export const STAGING_OAUTH_CONTAINER_ID = 'ebc3b6f8-f71e-498b-9fc3-61b997b8c14d';
export const PROD_OAUTH_CONTAINER_ID = 'ac2bd12b-a2b9-44d4-bd45-9f3ec59e5a0e';
export const MONDAY_OAUTH_SERVICE_KEY = 'X2C_MONDAY';
export const ASANA_OAUTH_SERVICE_KEY = 'X2C_ASANA';
export const CLICKUP_OAUTH_SERVICE_KEY = 'X2C_CLICKUP';

const OUTBOUND_ENDPOINTS: Partial<Record<SupportedThirdParty, Record<'prod' | 'staging', string>>> =
	{
		[MONDAY]: {
			prod: `outboundAuth/start?containerId=${PROD_OAUTH_CONTAINER_ID}&serviceKey=${MONDAY_OAUTH_SERVICE_KEY}`,
			staging: `outboundAuth/start?containerId=${STAGING_OAUTH_CONTAINER_ID}&serviceKey=${MONDAY_OAUTH_SERVICE_KEY}`,
		},
		[ASANA]: {
			prod: `outboundAuth/start?containerId=${PROD_OAUTH_CONTAINER_ID}&serviceKey=${ASANA_OAUTH_SERVICE_KEY}`,
			staging: `outboundAuth/start?containerId=${STAGING_OAUTH_CONTAINER_ID}&serviceKey=${ASANA_OAUTH_SERVICE_KEY}`,
		},
		[CLICK_UP]: {
			prod: `outboundAuth/start?containerId=${PROD_OAUTH_CONTAINER_ID}&serviceKey=${CLICKUP_OAUTH_SERVICE_KEY}`,
			staging: `outboundAuth/start?containerId=${STAGING_OAUTH_CONTAINER_ID}&serviceKey=${CLICKUP_OAUTH_SERVICE_KEY}`,
		},
	};

export type Props = {
	thirdParty: SupportedThirdParty;
	onSuccess: (importType?: SupportedThirdParty) => void;
	onFail?: () => void;
};

export const useThirdPartyOAuth = () => {
	const env = useEnvironment();
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.THIRD_PARTY_OAUTH,
	);

	const getUrl = useCallback(
		({ thirdParty, isProd }: { thirdParty: SupportedThirdParty; isProd: boolean }) => {
			const endpoint =
				OUTBOUND_ENDPOINTS[thirdParty] !== undefined &&
				OUTBOUND_ENDPOINTS[thirdParty]?.[isProd ? 'prod' : 'staging'];
			return endpoint && `${getATLContextUrl('id')}/${endpoint}`;
		},
		[],
	);

	return useCallback(
		async ({ thirdParty, onSuccess, onFail }: Props) => {
			const isProd = env === 'prod';

			const url = getUrl({ thirdParty, isProd });

			if (!url) {
				return Promise.reject(
					new Error(`Not supported third party: ${thirdParty} or environment: ${env}`),
				);
			}
			onExperienceStart();
			return auth(url)
				.then(() => {
					onSuccess(thirdParty);
					onExperienceSuccess();
				})
				.catch((error) => {
					onFail && onFail();
					onExperienceFail(APP_IDENTIFIER, error, {
						thirdParty,
					});
					fireErrorAnalytics({
						meta: {
							id: 'third-party-oAuth',
							packageName: APP_IDENTIFIER,
							teamName: APP_TEAM_NAME,
						},
						sendToPrivacyUnsafeSplunk: true,
						error,
						attributes: {
							thirdParty,
							error,
						},
					});
				});
		},
		[env, getUrl, onExperienceFail, onExperienceStart, onExperienceSuccess],
	);
};
